/**
 * Template Library GraphQL Queries and Mutations
 *
 * Complete GraphQL API for Template Marketplace operations.
 *
 * FERP Principles:
 * - Repository pattern: All template access through GraphQL API
 * - camelCase naming: GraphQL fields follow FERP convention
 * - Backend ID generation: template_id from Snowflake system
 * - Data-driven: All template metadata from backend
 *
 * Issue: #312 - White-Label System Implementation (Phase 2)
 */

import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Fragments
// ============================================================================

export const TEMPLATE_LIBRARY_ENTRY_FRAGMENT = gql`
  fragment TemplateLibraryEntryFragment on TemplateLibraryEntry {
    templateId
    templateName
    templateDescription
    category
    industry
    tags
    visibility
    ownerTenantId
    templateMetadata
    cloneCount
    rating
    reviewCount
    thumbnailUrl
    demoUrl
    isPremium
    price
    createdAt
    updatedAt
    publishedBy
    version
  }
`;

// ============================================================================
// Queries
// ============================================================================

export const LIST_TEMPLATE_LIBRARY = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  query ListTemplateLibrary($filters: TemplateLibraryFilters) {
    listTemplateLibrary(filters: $filters) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

export const GET_TEMPLATE_LIBRARY_ENTRY = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  query GetTemplateLibraryEntry($templateId: String!) {
    getTemplateLibraryEntry(templateId: $templateId) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

export const SEARCH_TEMPLATE_LIBRARY = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  query SearchTemplateLibrary($query: String!) {
    searchTemplateLibrary(query: $query) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

export const GET_TEMPLATES_BY_INDUSTRY = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  query GetTemplatesByIndustry($industry: String!) {
    getTemplatesByIndustry(industry: $industry) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

export const GET_TEMPLATES_BY_CATEGORY = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  query GetTemplatesByCategory($category: String!) {
    getTemplatesByCategory(category: $category) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

// ============================================================================
// Mutations
// ============================================================================

export const PUBLISH_TEMPLATE_TO_LIBRARY = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  mutation PublishTemplateToLibrary($input: PublishTemplateInput!) {
    publishTemplateToLibrary(input: $input) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

export const CLONE_TEMPLATE_FROM_LIBRARY = gql`
  mutation CloneTemplateFromLibrary($input: CloneTemplateInput!) {
    cloneTemplateFromLibrary(input: $input) {
      success
      newTemplateId
      templateName
      message
    }
  }
`;

export const RATE_TEMPLATE = gql`
  ${TEMPLATE_LIBRARY_ENTRY_FRAGMENT}
  mutation RateTemplate($input: RateTemplateInput!) {
    rateTemplate(input: $input) {
      ...TemplateLibraryEntryFragment
    }
  }
`;

export const DELETE_TEMPLATE_FROM_LIBRARY = gql`
  mutation DeleteTemplateFromLibrary($templateId: String!) {
    deleteTemplateFromLibrary(templateId: $templateId)
  }
`;

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Template Visibility Enum
 *
 * Determines who can see and clone templates:
 * - PUBLIC: Anyone can see and clone
 * - PRIVATE: Only owner tenant can see
 * - PREMIUM: Requires payment to clone
 */
export enum TemplateVisibility {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
  PREMIUM = 'PREMIUM',
}

/**
 * Template Library Entry
 *
 * Complete template marketplace entry with metadata, ratings, and usage stats.
 *
 * DynamoDB Storage:
 * - PK: TEMPLATE_LIBRARY
 * - SK: TEMPLATE#{template_id}
 * - GSI1: INDUSTRY#{industry} / CATEGORY#{category}#TEMPLATE#{template_id}
 * - GSI2: CATEGORY#{category} / RATING#{inverted_rating}#TEMPLATE#{template_id}
 */
export interface TemplateLibraryEntry {
  /** Template ID (backend Snowflake ID) */
  templateId: string;

  /** Template name (e.g., "Patient Registration Workflow") */
  templateName: string;

  /** Detailed description of template functionality */
  templateDescription: string;

  /** Category (intake, assessment, billing, etc.) */
  category: string;

  /** Industry (healthcare, dental, legal, finance) */
  industry: string;

  /** Tags for search/filtering (e.g., ["hipaa", "patient-intake", "dental"]) */
  tags: string[];

  /** Visibility level (PUBLIC, PRIVATE, PREMIUM) */
  visibility: TemplateVisibility;

  /** Owner tenant ID (for private templates) */
  ownerTenantId?: string | null;

  /** Complete template metadata (Metadata DSL v1.0) */
  templateMetadata: any; // WorkflowTemplateMetadata JSON

  /** Total number of times template has been cloned */
  cloneCount: number;

  /** Average rating (0.0 - 5.0) */
  rating: number;

  /** Total number of reviews */
  reviewCount: number;

  /** Thumbnail image URL */
  thumbnailUrl: string;

  /** Demo video/page URL (optional) */
  demoUrl?: string | null;

  /** Whether template requires payment */
  isPremium: boolean;

  /** Price in USD (for premium templates) */
  price?: number | null;

  /** Creation timestamp */
  createdAt: string; // ISO 8601 datetime

  /** Last update timestamp */
  updatedAt: string; // ISO 8601 datetime

  /** User ID of publisher */
  publishedBy: string;

  /** Template version (semantic versioning) */
  version: string;
}

/**
 * Template Library Filters
 *
 * Optional filters for listing templates.
 * All filters are applied with AND logic.
 */
export interface TemplateLibraryFilters {
  /** Filter by category (exact match) */
  category?: string | null;

  /** Filter by industry (exact match) */
  industry?: string | null;

  /** Filter by tags (any match) */
  tags?: string[] | null;

  /** Filter by visibility level */
  visibility?: TemplateVisibility | null;

  /** Limit results (default: all) */
  limit?: number | null;
}

/**
 * Publish Template Input
 *
 * Input for publishing a new template to the marketplace.
 * Template ID is generated by backend (Snowflake system).
 */
export interface PublishTemplateInput {
  /** Template name */
  templateName: string;

  /** Template description */
  templateDescription: string;

  /** Category (intake, assessment, billing, etc.) */
  category: string;

  /** Industry (healthcare, dental, legal, finance) */
  industry: string;

  /** Tags for search/filtering */
  tags: string[];

  /** Visibility level */
  visibility: TemplateVisibility;

  /** Owner tenant ID (for private templates) */
  ownerTenantId?: string | null;

  /** Template metadata (Metadata DSL v1.0) */
  templateMetadata: any; // WorkflowTemplateMetadata JSON

  /** Thumbnail URL */
  thumbnailUrl: string;

  /** Demo URL (optional) */
  demoUrl?: string | null;

  /** Is premium template */
  isPremium: boolean;

  /** Price (USD, for premium templates) */
  price?: number | null;

  /** Version */
  version: string;
}

/**
 * Clone Template Input
 *
 * Input for cloning a template from the marketplace to tenant's workspace.
 */
export interface CloneTemplateInput {
  /** Template ID to clone (must exist and be accessible) */
  templateId: string;

  /** Tenant ID (who is cloning) */
  tenantId: string;

  /** New template name (optional, uses original if not provided) */
  newTemplateName?: string | null;

  /** Customize after cloning (open in editor) */
  customize: boolean;
}

/**
 * Clone Template Result
 *
 * Result of cloning operation with new template ID.
 */
export interface CloneTemplateResult {
  /** Success flag */
  success: boolean;

  /** New template ID (backend Snowflake ID) */
  newTemplateId: string;

  /** Template name */
  templateName: string;

  /** Result message */
  message: string;
}

/**
 * Rate Template Input
 *
 * Input for rating a template (1-5 stars).
 */
export interface RateTemplateInput {
  /** Template ID */
  templateId: string;

  /** Rating (1-5) */
  rating: number;
}

// ============================================================================
// Query/Mutation Result Types
// ============================================================================

export interface ListTemplateLibraryData {
  listTemplateLibrary: TemplateLibraryEntry[];
}

export interface ListTemplateLibraryVars {
  filters?: TemplateLibraryFilters | null;
}

export interface GetTemplateLibraryEntryData {
  getTemplateLibraryEntry: TemplateLibraryEntry | null;
}

export interface GetTemplateLibraryEntryVars {
  templateId: string;
}

export interface SearchTemplateLibraryData {
  searchTemplateLibrary: TemplateLibraryEntry[];
}

export interface SearchTemplateLibraryVars {
  query: string;
}

export interface GetTemplatesByIndustryData {
  getTemplatesByIndustry: TemplateLibraryEntry[];
}

export interface GetTemplatesByIndustryVars {
  industry: string;
}

export interface GetTemplatesByCategoryData {
  getTemplatesByCategory: TemplateLibraryEntry[];
}

export interface GetTemplatesByCategoryVars {
  category: string;
}

export interface PublishTemplateToLibraryData {
  publishTemplateToLibrary: TemplateLibraryEntry;
}

export interface PublishTemplateToLibraryVars {
  input: PublishTemplateInput;
}

export interface CloneTemplateFromLibraryData {
  cloneTemplateFromLibrary: CloneTemplateResult;
}

export interface CloneTemplateFromLibraryVars {
  input: CloneTemplateInput;
}

export interface RateTemplateData {
  rateTemplate: TemplateLibraryEntry;
}

export interface RateTemplateVars {
  input: RateTemplateInput;
}

export interface DeleteTemplateFromLibraryData {
  deleteTemplateFromLibrary: boolean;
}

export interface DeleteTemplateFromLibraryVars {
  templateId: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for template visibility
 */
export function getVisibilityLabel(visibility: TemplateVisibility): string {
  switch (visibility) {
    case TemplateVisibility.PUBLIC:
      return 'Public';
    case TemplateVisibility.PRIVATE:
      return 'Private';
    case TemplateVisibility.PREMIUM:
      return 'Premium';
    default:
      return 'Unknown';
  }
}

/**
 * Get color for template visibility badge
 */
export function getVisibilityColor(visibility: TemplateVisibility): string {
  switch (visibility) {
    case TemplateVisibility.PUBLIC:
      return 'green';
    case TemplateVisibility.PRIVATE:
      return 'gray';
    case TemplateVisibility.PREMIUM:
      return 'yellow';
    default:
      return 'gray';
  }
}

/**
 * Format template price
 */
export function formatPrice(price?: number | null): string {
  if (!price) return 'Free';
  return `$${price.toFixed(2)}`;
}

/**
 * Format rating display (e.g., "4.5 (10 reviews)")
 */
export function formatRating(rating: number, reviewCount: number): string {
  if (reviewCount === 0) return 'No reviews';
  return `${rating.toFixed(1)} (${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'})`;
}

/**
 * Check if template is accessible by tenant
 */
export function isTemplateAccessible(
  template: TemplateLibraryEntry,
  tenantId: string
): boolean {
  switch (template.visibility) {
    case TemplateVisibility.PUBLIC:
      return true;
    case TemplateVisibility.PRIVATE:
      return template.ownerTenantId === tenantId;
    case TemplateVisibility.PREMIUM:
      return true; // Accessible but requires payment
    default:
      return false;
  }
}

/**
 * Validate rating value (1-5)
 */
export function validateRating(rating: number): boolean {
  return rating >= 1 && rating <= 5 && Number.isInteger(rating);
}
